import type { SubscriptionPlanConfig as TypesSubscriptionPlanConfig } from 'fitify-types/src/types/subscription'
import { z } from 'fitify-types/src/zod'
import type { UserLocation } from 'fitify-ui/src/server/types/user-location'

export enum PlanType {
  REGULAR = 'regular',
  TRIAL = 'trial',
  DISCOUNTED = 'discounted',
}

export const subscriptionPlanSchema = z.object({
  locale: z.string(),
  currency: z.string(),
  priceTotalValue: z.number(),
  totalValue: z.number(),
  futurePriceTotalValue: z.number().optional(),
  intervalUnit: z.string().optional(),
  intervalLength: z.number().optional(),
  discountDurationLength: z.number().optional(),
  discountPercentValue: z.number().optional(),
  discountDurationUnit: z.string().optional(),
  id: z.string(),
  sku: z.string(),
  type: z.nativeEnum(PlanType).optional(),
})

export type SubscriptionPlanSchema = z.infer<typeof subscriptionPlanSchema>

export type SubscriptionPlan = SubscriptionPlanSchema & {
  config: SubscriptionPlanConfig
}

export type SubscriptionProvider = {
  activePlan: string | null
  arePlansLoaded: boolean
  defaultPeriodPlan: string | null
  isSubmitting?: boolean
  onPlanChange: (value: string) => void
  onSubmit: () => void
  plans: SubscriptionPlan[] | null
}

export type PaddleSubscriptionProvider = {
  isCheckoutAvailable: boolean
} & SubscriptionProvider

export type RunServerUpdate = (
  userFunction: () => Promise<unknown>
) => Promise<{ type: 'success' } | { type: 'error'; error?: unknown }>

export type StripeCheckoutFormProvider = {
  returnUrl: string
  onCheckoutCompleted?: () => void
  onCheckoutStarted?: () => void
  userLocation?: {
    isLoaded: boolean
    data?: UserLocation
  }
  activePlan?: string | null
  sessionPriceId?: string | null
  canUpdatePlanInPlace?: boolean
  isUpdatingPlan?: boolean
  applyInPlacePlanUpdate?: (
    priceId: string,
    runServerUpdate: RunServerUpdate
  ) => Promise<void>
}

export type StripeSubscriptionProvider = {
  checkoutError: string | null
  clientSecret: string | null
  isCheckoutAvailable: boolean
  sessionId: string | null
} & StripeCheckoutFormProvider &
  SubscriptionProvider

export type StripeOneTimePurchaseProvider = {
  checkoutError: string | null
  clientSecret: string | null
  isCheckoutAvailable: boolean
  isSubmitting?: boolean
  onSubmit: () => void
  singlePurchasePlan: SubscriptionPlan | undefined
} & StripeCheckoutFormProvider

export type SubscriptionPlanConfig = TypesSubscriptionPlanConfig & {
  badge?: string
  badgePlaceholder?: string
  badgeColor?: string
  isEnabled: boolean
}

export type SubscriptionPageConfig = {
  addOnEnabled?: boolean
  checkoutStyle?: string
  countdownExpirationMillis?: number
  defaultPeriod: string
  discountType?: string
  downSellPromo?: string
  flexDiscountId?: string
  gateway?: string
  paddleDiscountId?: string
  planNamingStyle?: string
  planSubtitle?: string
  planTitle?: string
  promoDiscountPercentage?: number
  promoImageUrl?: string
}
